/**
 * 統合チャットボットコンポーネント
 * 
 * ChatbotModal + ChatbotInterfaceを統合したチャットボットコンポーネント。
 * モーダル表示、フローティング表示、埋め込み表示の3つのモードに対応。
 * 自然言語でのスケジュール管理や職人アサインなどのAI支援機能を提供。
 * 
 * @author GenbaFlow Team
 * @version 1.0.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';
import { Card } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Progress } from '@/shared/ui/progress';
import {
  Bot,
  Send,
  Loader2,
  X,
  Sparkles,
  Zap,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  MessageCircle,
  Minimize2,
  Maximize2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/shared/lib/utils';
import { AIActionResult, ChatResponse } from '@/types';
import { aiApi } from '@/features/ai/api';
import { AIActionExecutionService } from '@/features/ai/actionsApi';
import { useAdminOnly } from '@/shared/hooks/useAdminOnly';

/**
 * ログレベル定義 - 現在は未使用
 */
// const LogLevel = {
//   DEBUG: 'DEBUG',
//   INFO: 'INFO',
//   WARN: 'WARN',
//   ERROR: 'ERROR'
// } as const;

// type LogLevel = typeof LogLevel[keyof typeof LogLevel];

/**
 * 簡素化ログユーティリティ
 */
class ChatbotLogger {
  private static instance: ChatbotLogger;
  private readonly isDevelopment = process.env.NODE_ENV === 'development';

  static getInstance(): ChatbotLogger {
    if (!ChatbotLogger.instance) {
      ChatbotLogger.instance = new ChatbotLogger();
    }
    return ChatbotLogger.instance;
  }

  info(message: string, data?: any): void {
    if (this.isDevelopment) {
      console.info(`[UnifiedChatbot] ${message}`, data || '');
    }
  }

  error(message: string, error?: any): void {
    if (this.isDevelopment) {
      console.error(`[UnifiedChatbot] ${message}`, error || '');
    }
  }

  // パフォーマンス測定用
  performance(message: string, startTime: number): void {
    if (this.isDevelopment) {
      const duration = Date.now() - startTime;
      console.info(`[UnifiedChatbot] ${message} - ${duration}ms`);
    }
  }
}

/**
 * 統合チャットボットコンポーネントのプロパティ
 */
export interface UnifiedChatbotProps {
  /** モーダル表示時の開閉状態 */
  isOpen?: boolean;
  /** モーダル閉じる時のコールバック */
  onClose?: () => void;
  /** チャット処理結果を受け取るコールバック */
  onResult?: (result: ChatResponse) => void;
  /** アクション実行結果を受け取るコールバック */
  onActionExecuted?: (results: AIActionResult[]) => void;
  /** 表示モード */
  mode?: 'modal' | 'floating' | 'embedded';
  /** 追加のCSSクラス名 */
  className?: string;
  /** フローティング表示時の位置 */
  position?: 'bottom-right' | 'bottom-left' | 'custom';
  /** 自動フォーカス有効化 */
  autoFocus?: boolean;
  /** クイックアクション表示有効化 */
  showQuickActions?: boolean;
}

/**
 * チャットメッセージの型定義
 */
interface ChatMessage {
  /** メッセージの一意ID */
  id: string;
  /** メッセージの種類 */
  type: 'user' | 'ai' | 'system';
  /** メッセージ内容 */
  content: string;
  /** タイムスタンプ */
  timestamp: Date;
  /** アクション実行結果（AIメッセージの場合） */
  actions?: AIActionResult[];
  /** ローディング状態 */
  isLoading?: boolean;
  /** 確認が必要な場合の情報 */
  needsConfirmation?: boolean;
  /** 提案された代替案 */
  suggestions?: string[];
  /** 代替案データ */
  alternatives?: Record<string, string[]>;
  /** 元のリクエスト（再実行用） */
  originalRequest?: string;
  /** セッションID（確認処理用） */
  sessionId?: string;
}

/**
 * クイックアクションの定義
 * よく使用される質問を事前定義
 */
const QUICK_ACTIONS = [
  "明日の職人",
  "スケジュール確認",
  "プロジェクト進捗",
  "車両配車状況",
  "今日の作業予定"
] as const;

/**
 * 統合チャットボットコンポーネント
 * 
 * @param props - コンポーネントのプロパティ
 * @returns 統合チャットボットコンポーネント
 */
export function UnifiedChatbot({
  isOpen = false,
  onClose,
  onResult,
  onActionExecuted,
  mode = 'modal',
  className = "",
  position = 'bottom-right',
  autoFocus = true,
  showQuickActions = true
}: UnifiedChatbotProps) {
  // Logger instance初期化
  const logger = ChatbotLogger.getInstance();

  // Admin権限チェック（開発環境では無効化）
  const { shouldShowAdminFeature, accessDeniedMessage } = useAdminOnly({ disableAdminCheck: true });

  // コンポーネント初期化ログ
  useEffect(() => {
    logger.info('Component initialized', { mode, shouldShowAdminFeature });
  }, []);

  const [input, setInput] = useState('');
  const [isFloatingOpen, setIsFloatingOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // 表示状態の制御
  const isVisible = mode === 'modal' ? isOpen : mode === 'floating' ? isFloatingOpen : true;

  // Admin権限に応じて初期メッセージを設定
  const initialMessage: ChatMessage = shouldShowAdminFeature
    ? {
      id: '1',
      type: 'ai',
      content: 'こんにちは！GenbaFlow AIアシスタントです。自然言語でスケジュール管理や職人アサインなどをお手伝いします。何でもお聞きください！',
      timestamp: new Date(),
    }
    : {
      id: '1',
      type: 'system',
      content: `申し訳ございません。${accessDeniedMessage}\n\n管理者権限をお持ちの方にお問い合わせください。`,
      timestamp: new Date(),
    };

  const [messages, setMessages] = useState<ChatMessage[]>([initialMessage]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  // 簡素化された状態変更（重要なもののみログ）
  // const setProcessingWithLog = (processing: boolean) => {
  //   if (processing) logger.info('Starting AI processing');
  //   setIsProcessing(processing);
  // };
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * メッセージ末尾への自動スクロール
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // フォーカス管理
  useEffect(() => {
    if (isVisible && autoFocus) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isVisible, autoFocus]);

  // プログレスバーアニメーション
  useEffect(() => {
    if (isProcessing) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 200);
      return () => clearInterval(interval);
    } else {
      setProgress(0);
    }
  }, [isProcessing]);

  /**
   * 確認承認処理 - 新しい確認APIを使用
   */
  const handleConfirmation = async (message: ChatMessage, confirmed: boolean) => {
    if (!message.sessionId) {
      logger.error('Session ID not found in message', message);
      return;
    }

    logger.info('Processing confirmation', {
      sessionId: message.sessionId,
      confirmed: confirmed
    });

    try {
      setIsProcessing(true);

      const response = await aiApi.processConfirmation({
        session_id: message.sessionId,
        approved: confirmed,
        reason: confirmed ? undefined : 'ユーザーによる拒否'
      });

      logger.info('Confirmation response received', response);

      // 新しいAIメッセージを追加
      const confirmationMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'ai',
        content: response.response || response.message,
        timestamp: new Date(),
        actions: response.actions ? response.actions.map((action: any) => ({
          success: true,
          message: action.message || 'アクションが正常に実行されました',
          action_type: action.type || 'unknown',
          data: action.data || null
        })) : undefined,
        // 承認後のレスポンスでは確認ボタンを表示しない
        needsConfirmation: false,
        sessionId: undefined
      };

      // 元のメッセージの確認状態を無効化して、新しいメッセージを追加
      setMessages(prev => {
        return [
          ...prev.map(msg =>
            msg.id === message.id
              ? { ...msg, needsConfirmation: false, sessionId: undefined }
              : msg
          ),
          confirmationMessage
        ];
      });

      // コールバック呼び出し
      onResult?.(response);

    } catch (error) {
      logger.error('Confirmation processing failed', error);

      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'ai',
        content: '確認処理中にエラーが発生しました。もう一度お試しください。',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * チャット送信処理
   */
  const handleSubmit = async () => {
    return await handleSubmitWithInput(input.trim());
  };

  /**
   * 指定された入力でチャット送信処理
   */
  const handleSubmitWithInput = async (inputText: string) => {
    const startTime = Date.now();

    logger.info('Chat submission initiated');

    // 入力バリデーション
    if (!inputText.trim()) {
      // logger.warn('Chat submission blocked: empty input');
      return;
    }

    if (isProcessing) {
      // logger.warn('Chat submission blocked: already processing');
      return;
    }

    if (!shouldShowAdminFeature) {
      // logger.warn('Chat submission blocked: no admin access');
      return;
    }

    const trimmedInput = inputText;
    // logger.debug('Input validation passed', {
    //   originalInput: input,
    //   trimmedInput,
    //   characterCount: trimmedInput.length
    // });

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: trimmedInput,
      timestamp: new Date(),
    };

    const loadingMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      type: 'ai',
      content: 'Processing...',
      timestamp: new Date(),
      isLoading: true,
    };

    // logger.debug('Creating user and loading messages', {
    //   userMessage: { id: userMessage.id, contentLength: userMessage.content.length },
    //   loadingMessage: { id: loadingMessage.id }
    // });

    setMessages(prev => [...prev, userMessage, loadingMessage]);
    setInput('');
    setIsProcessing(true);
    setProgress(10);

    try {
      // AI処理
      logger.info('Initiating AI API call');

      const apiStartTime = Date.now();
      // セッションIDを生成または取得
      const sessionId = `session_${Date.now()}`;

      const response = await aiApi.processChat({
        message: trimmedInput,
        session_id: sessionId,
        context: {
          user_role: 'admin',
          current_page: 'dashboard',
        }
      });

      logger.performance('AI API call completed', apiStartTime);
      logger.info('AI API response received');

      setProgress(60);

      // アクション実行結果は既にLangGraphサービスで完了済み
      // フロントエンドでの重複実行を避けるため、レスポンスから直接取得
      let actionResults: AIActionResult[] = [];

      if (response.actions && response.actions.length > 0) {
        // await_* 系は「確認待ち」を意味する制御アクションのため、
        // 実行結果としては扱わない（初回承認カードに成功表示を出さない）
        const rawActions = Array.isArray(response.actions) ? response.actions : [];
        const executedActions = rawActions.filter((action: any) => {
          const type = String(action?.type ?? '').toLowerCase();
          return type && !type.startsWith('await_');
        });

        if (executedActions.length > 0) {
          logger.info('Action results already processed by LangGraph service');

          // レスポンスからアクション結果を構築（成功文言のデフォルト付与はしない）
          actionResults = executedActions.map((action: any) => ({
            success: action.success ?? true,
            message: action.message ?? '',
            action_type: action.type ?? 'unknown',
            data: action.data ?? null
          } as AIActionResult));
        }
      }

      setProgress(100);

      // 確認フローの処理
      const needsConfirmation = response.needs_confirmation ||
        response.requires_confirmation ||
        (response.suggestions && response.suggestions.length > 0);

      logger.info('Confirmation check', {
        needs_confirmation: response.needs_confirmation,
        requires_confirmation: response.requires_confirmation,
        has_suggestions: response.suggestions && response.suggestions.length > 0,
        needsConfirmation: needsConfirmation,
        suggestions: response.suggestions,
        response: response
      });

      let messageContent = response.response || response.message;

      // 確認が必要な場合の処理
      if (needsConfirmation) {
        // バックエンド生成の詳細な確認メッセージを最優先で表示
        messageContent = response.response || response.message || '処理を実行してもよろしいでしょうか？';

        // operations（確認対象の具体内容）が返ってくる場合は詳細を整形して追記
        try {
          const rawActions: any[] = Array.isArray((response as any).actions) ? (response as any).actions : [];
          const confirmAction = rawActions.find((a: any) => String(a?.type || '').toLowerCase() === 'await_confirmation');
          const operations: any[] = Array.isArray(confirmAction?.operations) ? confirmAction.operations : [];

          if (operations.length > 0) {
            const detailLines = operations.map((op: any) => {
              const opType = String(op?.type || '').toUpperCase();
              const d = op?.data || {};
              const craftsman = d.craftsman_name || d.craftsman || d.craftsman_names;
              const project = d.project_name || d.project;
              const zone = d.zone_name || d.zone;
              const dateVal = d.date || d.assigned_date;
              const vehicle = d.vehicle_type || d.truck_name || d.truck;
              const driver = d.driver_name;

              if (opType.includes('ASSIGN_CRAFTSMAN')) {
                return `- 職人: ${craftsman ?? '-'} / プロジェクト: ${project ?? '-'} / 工区: ${zone ?? '-'} / 日付: ${dateVal ?? '-'}`;
              }
              if (opType.includes('ASSIGN_VEHICLE')) {
                return `- 車両: ${vehicle ?? '-'} / 乗務員: ${driver ?? '-'} / プロジェクト: ${project ?? '-'} / 日付: ${dateVal ?? '-'}`;
              }
              return `- 種別: ${op?.type ?? 'unknown'} / データ: ${JSON.stringify(d)}`;
            }).join('\n');

            messageContent = `${messageContent}\n\n詳細:\n${detailLines}`;
          }
        } catch (e) {
          logger.error('Failed to render confirmation details', e);
        }

        // テキスト上の提案は表示せず、ボタンのみで承認/キャンセルを提示
        logger.info('Awaiting approval', {
          needsConfirmation: needsConfirmation,
          message: messageContent
        });
      }

      // 成功時の処理内容を適切に表示
      let finalMessageContent = messageContent;

      // アクションが実行された場合、成功メッセージを追加
      if (!needsConfirmation && actionResults.length > 0) {
        const successMessages = actionResults
          .filter(action => action.success && action.message)
          .map(action => action.message as string)
          .join('\n');

        if (successMessages) {
          finalMessageContent = `${messageContent}\n\n✅ 実行完了:\n${successMessages}`;
        }
      }

      // AIメッセージ更新
      const aiMessage: ChatMessage = {
        id: loadingMessage.id,
        type: 'ai',
        content: finalMessageContent,
        timestamp: new Date(),
        // 確認待ちの段階ではアクション結果を表示しない
        actions: !needsConfirmation && actionResults.length > 0 ? actionResults : undefined,
        needsConfirmation,
        suggestions: response.suggestions,
        alternatives: response.alternatives,
        originalRequest: needsConfirmation ? trimmedInput : undefined,
        sessionId: needsConfirmation ? sessionId : undefined,
      };

      logger.info('Creating AI message', {
        needsConfirmation: aiMessage.needsConfirmation,
        hasActions: !!aiMessage.actions,
        actionCount: aiMessage.actions?.length || 0,
        suggestions: aiMessage.suggestions
      });

      logger.info('Creating AI message', {
        needsConfirmation: aiMessage.needsConfirmation,
        hasActions: !!aiMessage.actions,
        actionCount: aiMessage.actions?.length || 0,
        suggestions: aiMessage.suggestions
      });

      // logger.debug('Creating AI response message', {
      //   messageId: aiMessage.id,
      //   contentLength: aiMessage.content.length,
      //   hasActions: !!aiMessage.actions,
      //   actionCount: aiMessage.actions?.length || 0
      // });

      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.id === loadingMessage.id ? aiMessage : msg
        );

        logger.info('Messages updated', {
          messageId: aiMessage.id,
          needsConfirmation: aiMessage.needsConfirmation,
          type: aiMessage.type,
          hasActions: !!aiMessage.actions,
          actionCount: aiMessage.actions?.length || 0
        });

        return updatedMessages;
      });

      // コールバック呼び出し
      // logger.debug('Invoking callbacks', {
      //   hasOnResult: !!onResult,
      //   hasOnActionExecuted: !!onActionExecuted,
      //   hasActionResults: actionResults.length > 0
      // });

      onResult?.(response);
      if (actionResults.length > 0) {
        onActionExecuted?.(actionResults);
        // logger.info('Action results callback executed', {
        //   actionCount: actionResults.length
        // });
      }

      logger.performance('Complete chat submission process', startTime);
      logger.info('Chat submission completed successfully');

    } catch (error) {
      logger.error('Chat processing failed', error);

      const errorMessage: ChatMessage = {
        id: loadingMessage.id,
        type: 'ai',
        content: '申し訳ございません。解析に失敗しました。もう一度お試しください。',
        timestamp: new Date(),
      };

      // logger.debug('Creating error message', {
      //   messageId: errorMessage.id,
      //   errorContent: errorMessage.content
      // });

      setMessages(prev => prev.map(msg =>
        msg.id === loadingMessage.id ? errorMessage : msg
      ));
    } finally {
      // logger.debug('Cleanup: resetting processing state');
      setIsProcessing(false);
      setTimeout(() => {
        // logger.debug('Cleanup: resetting progress');
        setProgress(0);
      }, 500);
    }
  };

  /**
   * キーボードイベント処理
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    // logger.debug('Keyboard event detected', {
    //   key: e.key,
    //   shiftKey: e.shiftKey,
    //   ctrlKey: e.ctrlKey,
    //   inputLength: input.length
    // });

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // logger.info('Enter key pressed - initiating chat submission');
      handleSubmit();
    }
  };

  /**
   * チャット履歴クリア
   */
  const handleClear = () => {
    logger.info('Chat history cleared');
    setMessages([messages[0]]); // 初期メッセージのみ残す
  };

  /**
   * チャットボット閉じる処理
   */
  const handleClose = () => {
    // logger.info('Chatbot close requested', {
    //   mode,
    //   hasOnClose: !!onClose
    // });

    if (mode === 'modal' && onClose) {
      // logger.debug('Closing modal chatbot');
      onClose();
    } else if (mode === 'floating') {
      // logger.debug('Closing floating chatbot');
      setIsFloatingOpen(false);
    }
  };

  /**
   * クイックアクション選択処理
   */
  const handleQuickAction = (suggestion: string) => {
    // logger.info('Quick action selected', {
    //   suggestion,
    //   suggestionLength: suggestion.length
    // });
    setInput(suggestion);
  };

  // フローティングボタン表示
  if (mode === 'floating' && !isFloatingOpen) {
    const positionClasses = {
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'custom': ''
    } as const;

    return (
      <div className={cn("fixed z-50", positionClasses[position], className)} data-testid="chatbot-interface">
        <Button
          onClick={() => setIsFloatingOpen(true)}
          className="rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-all duration-200 bg-blue-600 hover:bg-blue-700"
          size="icon"
        >
          <MessageCircle className="h-6 w-6 text-white" />
          <span className="sr-only">チャットボットを開く</span>
        </Button>
      </div>
    );
  }

  // フローティングウィンドウ表示
  if (mode === 'floating' && isFloatingOpen) {
    const positionClasses = {
      'bottom-right': 'bottom-20 right-4',
      'bottom-left': 'bottom-20 left-4',
      'custom': ''
    } as const;

    return (
      <div className={cn(
        "fixed z-50 bg-white rounded-lg shadow-xl border flex flex-col",
        positionClasses[position],
        isExpanded ? "w-96 h-[600px]" : "w-80 h-96",
        className
      )} data-testid="chatbot-floating">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm">AI アシスタント</h3>
          </div>
          <div className="flex items-center gap-1">
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
            >
              {isExpanded ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
            </Button>
            <Button
              onClick={handleClear}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
            <Button
              onClick={handleClose}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* プログレスバー */}
        <AnimatePresence>
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="px-4"
            >
              <Progress value={progress} className="h-1" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* メッセージエリア */}
        <div className="flex-1 px-3 min-h-0 overflow-hidden">
          <div className="h-full overflow-y-auto py-3 space-y-3" style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db #f3f4f6',
            maxHeight: '300px',
            minHeight: '150px'
          }}>
            <AnimatePresence initial={false}>
              {messages.map((message) => {
                // メッセージレンダリングデバッグ
                logger.info('Rendering message', {
                  messageId: message.id,
                  type: message.type,
                  needsConfirmation: message.needsConfirmation,
                  hasActions: !!message.actions,
                  actionCount: message.actions?.length || 0
                });

                return (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className={cn(
                      "flex gap-2",
                      message.type === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    {message.type === 'ai' && (
                      <div className="h-6 w-6 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0 mt-1">
                        {message.isLoading ? (
                          <Loader2 className="h-3 w-3 text-white animate-spin" />
                        ) : (
                          <Bot className="h-3 w-3 text-white" />
                        )}
                      </div>
                    )}

                    <div className={cn(
                      "max-w-[75%] space-y-1",
                      message.type === 'user' ? 'items-end' : 'items-start'
                    )}>
                      <Card className={cn(
                        "p-2 shadow-sm text-xs",
                        message.type === 'user'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : message.isLoading
                            ? 'bg-gray-100 border-gray-200'
                            : 'bg-white border-gray-200'
                      )}>
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </Card>

                      {/* アクション結果表示 */}
                      {message.actions && message.actions.length > 0 && (
                        <div className="space-y-1">
                          {message.actions.map((action: AIActionResult, idx: number) => (
                            <Card key={idx} className="p-2 bg-green-50 border-green-200">
                              <div className="flex items-center gap-1">
                                {action.success ? (
                                  <CheckCircle className="h-3 w-3 text-green-600" />
                                ) : (
                                  <AlertCircle className="h-3 w-3 text-red-600" />
                                )}
                                <Badge
                                  variant={action.success ? 'default' : 'destructive'}
                                  className="text-xs h-4"
                                >
                                  {action.action_type}
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-700 mt-1">{action.message}</p>
                            </Card>
                          ))}
                        </div>
                      )}

                      {/* 確認ボタン */}
                      {(() => {
                        const shouldShowConfirmation = message.needsConfirmation && message.type === 'ai';

                        // 詳細デバッグ
                        logger.info('Confirmation button check - DETAILED', {
                          messageId: message.id,
                          needsConfirmation: message.needsConfirmation,
                          type: message.type,
                          shouldShowConfirmation,
                          messageObject: message
                        });

                        return shouldShowConfirmation;
                      })() && (
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              onClick={() => handleConfirmation(message, true)}
                              className="text-xs px-3 py-1 bg-green-600 hover:bg-green-700"
                            >
                              はい、実行する
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleConfirmation(message, false)}
                              className="text-xs px-3 py-1 border-gray-300"
                            >
                              キャンセル
                            </Button>
                          </div>
                        )}

                      <p className="text-xs text-gray-500">
                        {message.timestamp.toLocaleTimeString('ja-JP', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* 入力エリア */}
        <div className="border-t bg-white p-3 rounded-b-lg">
          <div className="flex gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={shouldShowAdminFeature ? "自然言語で指示を入力..." : "Admin権限が必要です"}
              disabled={isProcessing || !shouldShowAdminFeature}
              className="min-h-[40px] resize-none text-xs border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              rows={2}
            />
            <Button
              onClick={handleSubmit}
              disabled={!input.trim() || isProcessing || !shouldShowAdminFeature}
              size="sm"
              className="h-[40px] px-3 bg-blue-600 hover:bg-blue-700"
            >
              {isProcessing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </Button>
          </div>

          {/* クイックアクション */}
          {showQuickActions && isExpanded && (
            <div className="flex flex-wrap gap-1 mt-2">
              {QUICK_ACTIONS.slice(0, 3).map((suggestion) => (
                <Button
                  key={suggestion}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAction(suggestion)}
                  disabled={isProcessing}
                  className="text-xs h-6 px-2 text-gray-600 hover:text-blue-600 hover:border-blue-300"
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // モーダル表示
  if (mode === 'modal') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl h-[80vh] p-0" data-testid="chatbot-modal">
          {/* ラッパー: 縦レイアウト（ヘッダー/メッセージ/クイックアクション/入力） */}
          <div className="flex h-full flex-col">
            {/* ヘッダー */}
            <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <DialogTitle className="text-lg font-semibold text-gray-900">
                      AI アシスタント
                    </DialogTitle>
                    <p className="text-sm text-gray-600">
                      自然言語でスケジュール管理をサポート
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {messages.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClear}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  )}
                  <DialogClose asChild>
                    <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900">
                      <X className="h-4 w-4" />
                    </Button>
                  </DialogClose>
                </div>
              </div>
            </DialogHeader>

            {/* プログレスバー */}
            <AnimatePresence>
              {isProcessing && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-6 flex-shrink-0"
                >
                  <Progress value={progress} className="h-1" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* メッセージエリア - 残りのスペースを全て使用 */}
            <div className="flex-1 overflow-y-auto px-6 min-h-0">
              <div className="py-4 space-y-4">
                <AnimatePresence initial={false}>
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className={cn(
                        "flex gap-3",
                        message.type === 'user' ? 'justify-end' : 'justify-start'
                      )}
                    >
                      {message.type === 'ai' && (
                        <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0 mt-1">
                          {message.isLoading ? (
                            <Loader2 className="h-4 w-4 text-white animate-spin" />
                          ) : (
                            <Bot className="h-4 w-4 text-white" />
                          )}
                        </div>
                      )}

                      <div className={cn(
                        "max-w-[80%] space-y-2",
                        message.type === 'user' ? 'items-end' : 'items-start'
                      )}>
                        <Card className={cn(
                          "p-3 shadow-sm",
                          message.type === 'user'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : message.isLoading
                              ? 'bg-gray-100 border-gray-200'
                              : 'bg-white border-gray-200'
                        )}>
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </Card>

                        {/* アクション結果表示 */}
                        {message.actions && message.actions.length > 0 && (
                          <div className="space-y-2">
                            {message.actions.map((action: AIActionResult, idx: number) => (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: idx * 0.1 }}
                              >
                                <Card className="p-3 bg-green-50 border-green-200">
                                  <div className="flex items-center gap-2">
                                    {action.success ? (
                                      <CheckCircle className="h-4 w-4 text-green-600" />
                                    ) : (
                                      <AlertCircle className="h-4 w-4 text-red-600" />
                                    )}
                                    <Badge
                                      variant={action.success ? 'default' : 'destructive'}
                                      className="text-xs"
                                    >
                                      {action.action_type}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-700 mt-1">{action.message ?? ''}</p>
                                  {action.data &&
                                    typeof action.data === 'object' &&
                                    action.data !== null &&
                                    'craftsman_names' in action.data &&
                                    typeof (action.data as Record<string, unknown>).craftsman_names === 'string' ? (
                                    <p className="text-xs text-blue-600 mt-1">
                                      職人: {String((action.data as Record<string, unknown>).craftsman_names)}
                                    </p>
                                  ) : null}
                                </Card>
                              </motion.div>
                            ))}
                          </div>
                        )}

                        {/* 確認ボタン（モーダル版） */}
                        {message.needsConfirmation && message.type === 'ai' && (
                          <div className="flex gap-2 mt-3">
                            <Button
                              size="sm"
                              onClick={() => handleConfirmation(message, true)}
                              className="text-sm px-4 py-2 bg-green-600 hover:bg-green-700"
                            >
                              はい、実行する
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleConfirmation(message, false)}
                              className="text-sm px-4 py-2 border-gray-300"
                            >
                              キャンセル
                            </Button>
                          </div>
                        )}

                        <p className="text-xs text-gray-500">
                          {message.timestamp.toLocaleTimeString('ja-JP')}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* クイックアクション - メッセージエリアの下、入力エリアの上 */}
            {showQuickActions && (
              <div className="border-t bg-gray-50 px-6 py-3 flex-shrink-0">
                <div className="flex flex-wrap gap-2">
                  {QUICK_ACTIONS.map((suggestion) => (
                    <Button
                      key={suggestion}
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickAction(suggestion)}
                      disabled={isProcessing}
                      className="text-xs text-gray-600 hover:text-blue-600 hover:border-blue-300 whitespace-nowrap"
                    >
                      <Zap className="h-3 w-3 mr-1" />
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* 入力エリア - 最下部に固定 */}
            <div className="border-t bg-white px-6 py-4 rounded-b-lg flex-shrink-0">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={shouldShowAdminFeature ? "自然言語で指示を入力してください..." : "Admin権限が必要です"}
                    disabled={isProcessing || !shouldShowAdminFeature}
                    className="min-h-[60px] resize-none pr-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    rows={2}
                  />
                  {input.trim() && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute bottom-2 right-2"
                    >
                      <Sparkles className="h-4 w-4 text-blue-500" />
                    </motion.div>
                  )}
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={!input.trim() || isProcessing || !shouldShowAdminFeature}
                  size="lg"
                  className="h-[60px] px-6 bg-blue-600 hover:bg-blue-700"
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // 埋め込み表示
  return (
    <div className={cn("bg-white rounded-lg border flex flex-col h-96", className)}>
      {/* ヘッダー */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900">AI アシスタント</h3>
        </div>
        <Button
          onClick={handleClear}
          variant="ghost"
          size="sm"
          className="text-gray-600 hover:text-gray-900"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* メッセージエリア */}
      <div className="flex-1 px-4 min-h-0 overflow-hidden">
        <div className="h-full overflow-y-auto py-4 space-y-3" style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#d1d5db #f3f4f6',
          maxHeight: '400px',
          minHeight: '200px'
        }}>
          <AnimatePresence initial={false}>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className={cn(
                  "flex gap-2",
                  message.type === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {message.type === 'ai' && (
                  <div className="h-6 w-6 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0 mt-1">
                    {message.isLoading ? (
                      <Loader2 className="h-3 w-3 text-white animate-spin" />
                    ) : (
                      <Bot className="h-3 w-3 text-white" />
                    )}
                  </div>
                )}

                <div className={cn(
                  "max-w-[80%] space-y-1",
                  message.type === 'user' ? 'items-end' : 'items-start'
                )}>
                  <Card className={cn(
                    "p-2 shadow-sm text-sm",
                    message.type === 'user'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : message.isLoading
                        ? 'bg-gray-100 border-gray-200'
                        : 'bg-white border-gray-200'
                  )}>
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </Card>

                  <p className="text-xs text-gray-500">
                    {message.timestamp.toLocaleTimeString('ja-JP', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* 入力エリア */}
      <div className="border-t bg-white p-3 rounded-b-lg">
        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={shouldShowAdminFeature ? "自然言語で入力..." : "Admin権限が必要です"}
            disabled={isProcessing || !shouldShowAdminFeature}
            className="min-h-[40px] resize-none text-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            rows={2}
          />
          <Button
            onClick={handleSubmit}
            disabled={!input.trim() || isProcessing || !shouldShowAdminFeature}
            size="sm"
            className="h-[40px] px-3 bg-blue-600 hover:bg-blue-700"
          >
            {isProcessing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default UnifiedChatbot;